import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  useGetPayrollsQuery,
  useCreatePayrollMutation,
  useDeletePayrollMutation,
} from "@/store/payrolls/apis";
import { Loading } from "@/components/Loading";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
  faPlus,
  faTrash,
} from "@awesome.me/kit-9096fb77ac/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CrewNavigation } from "@/components/Crew/tabs";

const Payroll = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { data: payrolls, isLoading, refetch } = useGetPayrollsQuery();
  const [createPayroll] = useCreatePayrollMutation();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [deletePayroll] = useDeletePayrollMutation();

  useEffect(() => {
    if (location.state?.refresh) {
      refetch();
    }
  }, [location.state, refetch]);

  const handleCreatePayroll = async () => {
    try {
      const result = await createPayroll({
        data: {
          start_date: startDate,
          end_date: endDate,
        },
      }).unwrap();

      toast({
        title: "Success",
        description: "Payroll created successfully",
      });

      setShowCreateDialog(false);
      navigate(`/payroll/${result.id}`);
    } catch {
      toast({
        title: "Error",
        description: "Failed to create payroll",
        variant: "destructive",
      });
    }
  };

  const handleDeletePayroll = async (id: number) => {
    try {
      await deletePayroll({ id });
      refetch();

      toast({
        title: "Success",
        description: "Payroll deleted successfully",
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to delete payroll",
        variant: "destructive",
      });
    }
  };

  if (isLoading) return <Loading />;

  return (
    <>
      <div className="px-6 py-2">
        <CrewNavigation />
      </div>
      <div className="mt-2 px-6 max-w-screen-lg">
        <div className="flex items-baseline gap-3 mb-6">
          <h1 className="text-2xl font-bold">Payroll</h1>
          <Button
            variant="ghost"
            onClick={() => setShowCreateDialog(true)}
            className="text-black flex items-center gap-1 text-base"
          >
            <FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
            Create Payroll
          </Button>
        </div>

        <div className="bg-white rounded-lg">
          <div className="grid grid-cols-12 gap-4 p-4 font-medium border-b uppercase text-xs text-gray-700 text-right">
            <div className="col-span-4 text-left">Period</div>
            <div className="col-span-2">Hours</div>
            <div className="col-span-2">Pay</div>
            <div className="col-span-2">Created On</div>
            <div className="col-span-2"></div>
          </div>
          {payrolls?.map((payroll) => (
            <div
              key={payroll.id}
              className="grid grid-cols-12 gap-4 px-4 py-2 border-b hover:bg-gray-50 cursor-pointer text-sm items-center text-right"
              onClick={() => navigate(`/payroll/${payroll.id}`)}
            >
              <div className="col-span-4 text-left">
                {format(parseISO(payroll.start_date), "M/dd/yyyy (EE)")} -{" "}
                {format(parseISO(payroll.end_date), "M/dd/yyyy (EE)")}
              </div>
              <div className="col-span-2">{payroll.hours} hours</div>
              <div className="col-span-2">
                {Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(payroll.pay)}
              </div>
              <div className="col-span-2">
                {format(parseISO(payroll.created_at), "MMM d, yyyy")}
              </div>
              <div className="col-span-2 flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 flex items-center gap-1"
                  onClick={(e) => {
                    e.stopPropagation();

                    if (
                      confirm(
                        "Are you sure you want to delete this payroll? You will need to re-create the payroll for these dates."
                      )
                    ) {
                      handleDeletePayroll(payroll.id);
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  Delete Payroll
                </Button>
              </div>
            </div>
          ))}
        </div>

        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Payroll</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <Button onClick={handleCreatePayroll} className="w-full">
                Create Payroll
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default Payroll;
